import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Button, Modal, Badge } from "react-bootstrap";
import pdfIcon from "../../assets/images/pdf-icon.png";
import { IoEyeOutline } from "react-icons/io5";
import { FaCheckCircle, FaPlus } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { AWSUpload } from "../../Upload/awsupload";
import { toast } from "react-toastify";

const createEmptyUpload = () => ({
  fileUrl: "",
  customData: "",
  proofofexperienceVerified: false,
});

const MultipleFileUpload = ({
  data,
  outerIndex,
  handleInputChange,
  fieldErrors,
  setFieldErrors,
  style,
  checkErrorFn,
  applicationDetails,
  applicationID,
}) => {
  const [uploads, setUploads] = useState(() => {
    if (data?.proofOfExperienceDocument?.length) {
      return [...data.proofOfExperienceDocument];
    }
    return [createEmptyUpload()];
  });
  const proofDocs = data?.proofOfExperienceDocument;
  const [showIframe, setShowIframe] = useState(null);
  const [errorDoc, setErrorDoc] = useState([]);

  // refs for file inputs
  const fileInputRefs = useRef([]);

  // Load uploads from sessionStorage on mount
  useEffect(() => {
    if (applicationID) {
      const savedUploads = sessionStorage.getItem(
        `proofOfExperience_${applicationID}_${outerIndex}`
      );
      if (savedUploads) {
        const parsedUploads = JSON.parse(savedUploads);
        // Deduplicate uploads based on fileUrl to prevent duplicates
        const deduplicatedUploads = parsedUploads.filter(
          (upload, index, self) =>
            index ===
            self.findIndex(
              (u) => u.fileUrl === upload.fileUrl && u.fileUrl !== ""
            )
        );
        setUploads(
          deduplicatedUploads.length
            ? deduplicatedUploads
            : [createEmptyUpload()]
        );
        // Update parent state with deduplicated data
        handleInputChange(
          outerIndex,
          null,
          "proofOfExperienceDocument",
          deduplicatedUploads.length
            ? deduplicatedUploads
            : [createEmptyUpload()]
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationID, outerIndex]);

  // Save uploads to sessionStorage whenever they change
  useEffect(() => {
    if (applicationID) {
      // Deduplicate uploads before saving to prevent duplicates
      const deduplicatedUploads = uploads.filter(
        (upload, index, self) =>
          index ===
          self.findIndex(
            (u) => u.fileUrl === upload.fileUrl && u.fileUrl !== ""
          )
      );
      sessionStorage.setItem(
        `proofOfExperience_${applicationID}_${outerIndex}`,
        JSON.stringify(deduplicatedUploads)
      );
    }
  }, [uploads, applicationID, outerIndex]);

  // Update uploads when data.proofOfExperienceDocument changes from parent
  useEffect(() => {
    if (!Array.isArray(proofDocs)) {
      return;
    }

    // Deduplicate proofDocs to prevent duplicates from parent
    const deduplicatedProofDocs = proofDocs.filter(
      (upload, index, self) =>
        index ===
        self.findIndex((u) => u.fileUrl === upload.fileUrl && u.fileUrl !== "")
    );

    // Preserve verification status from current uploads when merging with parent data
    const nextUploads = deduplicatedProofDocs.length
      ? deduplicatedProofDocs.map((parentUpload) => {
          // Find matching upload in current state to preserve verification status
          const existingUpload = uploads.find(
            (currentUpload) => currentUpload.fileUrl === parentUpload.fileUrl
          );

          // If we have an existing upload with verification status, preserve it
          if (existingUpload && existingUpload.fileUrl) {
            return {
              ...parentUpload,
              proofofexperienceVerified:
                existingUpload.proofofexperienceVerified,
            };
          }

          // Otherwise, use parent data as-is
          return parentUpload;
        })
      : [createEmptyUpload()];

    setUploads((prevUploads) => {
      const prevSnapshot = JSON.stringify(prevUploads);
      const nextSnapshot = JSON.stringify(nextUploads);
      if (prevSnapshot === nextSnapshot) {
        return prevUploads;
      }

      return nextUploads;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofDocs]);

  // Local validation function
  const localCheckErrorFn = (fieldKey, value, customMessage) => {
    let errors = [];

    if (!value) {
      const defaultMessage = fieldKey.includes("proofofexperience_")
        ? "Please upload a file."
        : "This field is required.";
      errors.push(customMessage || defaultMessage);
    }

    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      [fieldKey]: {
        errors: errors,
        touched: true,
      },
    }));

    return errors.length === 0;
  };

  // Validate verification status on uploads change
  useEffect(() => {
    uploads.forEach((upload, index) => {
      if (!upload.proofofexperienceVerified && upload.fileUrl) {
        // Only set error if the user has uploaded a file but hasn't verified
        setFieldErrors((prevErrors) => ({
          ...prevErrors,
          [`fileNotVerified${outerIndex}${index}`]: {
            errors: ["File must be verified."],
            touched: true,
          },
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads]);

  // Validator now accepts PDFs and common images
  const isValidFile = (file, index, setErrorDoc) => {
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (!file) return false;

    const allowedMime = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    if (!allowedMime.includes(file.type)) {
      const msg = "Unsupported file type. Only PDF and JPG/PNG allowed.";
      toast.warn(`${file.name}: ${msg}`);
      setErrorDoc((prev) => {
        const newErrors = [...(prev || [])];
        newErrors[index] = msg;
        return newErrors;
      });
      return false;
    }

    if (file.size > maxSize) {
      const msg = "File exceeds 2 MB size limit.";
      toast.warn(`${file.name}: ${msg}`);
      setErrorDoc((prev) => {
        const newErrors = [...(prev || [])];
        newErrors[index] = msg;
        return newErrors;
      });
      return false;
    }

    // Clear error if valid
    setErrorDoc((prev) => {
      const newErrors = [...(prev || [])];
      newErrors[index] = "";
      return newErrors;
    });

    return true;
  };

  const handleUploadFile = async (event, index) => {
    // clear input value early so same file can be reselected later
    const file = event.target.files?.[0];
    event.target.value = null;

    if (!file || !isValidFile(file, index, setErrorDoc)) {
      return;
    }

    const fileUrl = await AWSUpload(file);

    // Check if this file URL already exists to prevent duplicates
    const existingUpload = uploads.find((upload) => upload.fileUrl === fileUrl);
    if (existingUpload) {
      toast.warn("This file has already been uploaded");
      return;
    }

    const updatedUploads = [...uploads];
    updatedUploads[index] = {
      ...updatedUploads[index],
      fileUrl,
      proofofexperienceVerified: false,
    };

    // Deduplicate the updated uploads array
    const deduplicatedUploads = updatedUploads.filter(
      (upload, idx, self) =>
        idx ===
        self.findIndex((u) => u.fileUrl === upload.fileUrl && u.fileUrl !== "")
    );

    setUploads(deduplicatedUploads);
    // update parent only after a valid file is uploaded
    handleInputChange(
      outerIndex,
      null,
      "proofOfExperienceDocument",
      deduplicatedUploads
    );

    localCheckErrorFn(`proofofexperience_${outerIndex}${index}`, fileUrl);
  };

  // Handle input change for custom data (file name)
  const handleCustomDataChange = (event, index) => {
    const { value } = event.target;

    const updatedUploads = [...uploads];
    updatedUploads[index] = {
      ...updatedUploads[index],
      customData: value,
    };

    setUploads(updatedUploads);
    // Update parent state (custom name change) - parent probably expects this
    handleInputChange(
      outerIndex,
      null,
      "proofOfExperienceDocument",
      updatedUploads
    );

    // Validation will be triggered onBlur
  };

  // Add new upload data
  const handleAddUpload = () => {
    const updatedUploads = [...uploads, createEmptyUpload()];

    setUploads(updatedUploads);

    // IMPORTANT: do not call handleInputChange here with an empty placeholder.
    // If the parent sanitizes/filters out empty uploads it will send back a prop
    // that removes the placeholder, causing the "Choose Files" UI to disappear.
    // We'll update the parent only when a real file is uploaded (see handleUploadFile).
    setErrorDoc((prevErrors) => [...(prevErrors || []), ""]);

    // ensure a slot exists in the refs array for the new input
    fileInputRefs.current = fileInputRefs.current || [];
    // push a null placeholder so fileInputRefs.current[index] exists after render
    fileInputRefs.current.push(null);
  };

  // Delete upload data
  const handleDeleteUpload = (index) => {
    const uploadToDelete = uploads[index];

    // Filter out the upload at the specified index
    const filteredUploads = uploads.filter((_, i) => i !== index);
    const nextUploads = filteredUploads.length
      ? filteredUploads
      : [createEmptyUpload()];

    setUploads(nextUploads);

    // Update parent state immediately
    handleInputChange(
      outerIndex,
      null,
      "proofOfExperienceDocument",
      nextUploads
    );

    // Update sessionStorage immediately to prevent reload issues
    if (applicationID) {
      sessionStorage.setItem(
        `proofOfExperience_${applicationID}_${outerIndex}`,
        JSON.stringify(nextUploads)
      );
    }

    // Clean up error states
    setErrorDoc((prevErrors) => {
      const nextErrors = (prevErrors || []).filter((_, i) => i !== index);
      if (nextUploads.length > nextErrors.length) {
        nextErrors.push("");
      }
      return nextErrors;
    });

    // Remove errors associated with this index
    setFieldErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[`fileName${outerIndex}${index}`];
      delete updatedErrors[`proofofexperience_${outerIndex}${index}`];
      delete updatedErrors[`fileNotVerified${outerIndex}${index}`];
      return updatedErrors;
    });

    // remove ref placeholder
    fileInputRefs.current = (fileInputRefs.current || []).filter(
      (_, i) => i !== index
    );

    // Show success message
    toast.success("Document deleted successfully");
  };

  // Confirm verification
  const confirmVerification = (index) => {
    const updatedUploads = [...uploads];
    updatedUploads[index].proofofexperienceVerified = true;

    setUploads(updatedUploads);

    // Update parent state immediately
    handleInputChange(
      outerIndex,
      null,
      "proofOfExperienceDocument",
      updatedUploads
    );

    // Update sessionStorage immediately to preserve verification status
    if (applicationID) {
      sessionStorage.setItem(
        `proofOfExperience_${applicationID}_${outerIndex}`,
        JSON.stringify(updatedUploads)
      );
    }

    // Remove verification error
    setFieldErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[`fileNotVerified${outerIndex}${index}`];
      return updatedErrors;
    });

    setShowIframe(null);

    // Show success message
    toast.success("Document verified successfully");
  };

  // Clear all uploads (useful for reset functionality)
  const clearAllUploads = () => {
    const emptyUploads = [createEmptyUpload()];
    setUploads(emptyUploads);
    handleInputChange(
      outerIndex,
      null,
      "proofOfExperienceDocument",
      emptyUploads
    );

    if (applicationID) {
      sessionStorage.setItem(
        `proofOfExperience_${applicationID}_${outerIndex}`,
        JSON.stringify(emptyUploads)
      );
    }

    setErrorDoc([""]);
    setFieldErrors({});
    fileInputRefs.current = [];
  };

  return (
    <>
      <div id="fileUpload">
        <label className="form-label inputBoxHeadForm" style={style.label}>
          Experience Certificate/Proof of Experience
          <span className="mx-1 text-danger">*</span>
        </label>
        {uploads.map((upload, index) => (
          <Row key={index} className="mt-1 pb-0 mb-0">
            <Col xs={12} md={2}>
              <div className="mb-0">
                <button
                  type="button"
                  style={{
                    borderRadius: "10px",
                    height: "40px",
                    padding: "5px 15px",
                    fontSize: "12px",
                    cursor: "pointer",
                    margin: "15px 0",
                    border: "none",
                    backgroundColor: "#921d1e",
                    color: "#fff",
                  }}
                  onClick={() => {
                    const refEl = fileInputRefs.current?.[index];
                    if (refEl) {
                      refEl.click();
                      return;
                    }
                    // fallback to getElementById, and if not present yet try again after a tick
                    const id = `proofofexperience_${outerIndex}${index}`;
                    const el = document.getElementById(id);
                    if (el) {
                      el.click();
                      return;
                    }
                    setTimeout(() => {
                      const el2 = document.getElementById(id);
                      el2?.click();
                    }, 50);
                  }}
                  onBlur={() =>
                    localCheckErrorFn(
                      `proofofexperience_${outerIndex}${index}`,
                      uploads[index]?.fileUrl
                    )
                  }
                >
                  <FaPlus /> <span className="ps-2">Choose Files</span>
                </button>

                <input
                  type="file"
                  id={`proofofexperience_${outerIndex}${index}`}
                  accept=".pdf,.jpg,.jpeg,.png"
                  // keep input off-screen instead of display:none (more reliable for programmatic click)
                  style={{ position: "absolute", left: "-9999px" }}
                  onChange={(e) => handleUploadFile(e, index)}
                  ref={(el) => (fileInputRefs.current[index] = el)}
                  onBlur={() =>
                    localCheckErrorFn(
                      `proofofexperience_${outerIndex}${index}`,
                      uploads[index]?.fileUrl
                    )
                  }
                />
                <br />
                {/* Display validation errors */}
                {fieldErrors?.[`proofofexperience_${outerIndex}${index}`]
                  ?.touched &&
                  fieldErrors?.[`proofofexperience_${outerIndex}${index}`]
                    ?.errors?.length > 0 && (
                    <span className="errorPresent fs-6" style={style.errorSpan}>
                      {
                        fieldErrors[`proofofexperience_${outerIndex}${index}`]
                          ?.errors[0]
                      }
                      <br />
                    </span>
                  )}

                {/* Display file-specific errors */}
                {errorDoc[index] && (
                  <>
                    <span className="errorPresent" style={style.errorSpan}>
                      {errorDoc[index]}
                    </span>
                    <br />
                  </>
                )}
              </div>
            </Col>

            <Col xs={12} md={10}>
              {/* File preview, verification, and delete buttons */}
              {upload.fileUrl && (
                <>
                  <Row>
                    <Col xs={12} md={1}>
                      <img
                        src={pdfIcon}
                        alt="PDF preview"
                        style={{
                          width: "40px",
                          cursor: "pointer",
                          marginTop: "15px",
                        }}
                        onClick={() => setShowIframe(index)}
                      />
                    </Col>
                    <Col xs={12} md={4}>
                      <input
                        style={style.input}
                        type="text"
                        className="mt-3"
                        placeholder="Enter File Name"
                        value={upload.customData}
                        onChange={(e) => handleCustomDataChange(e, index)}
                        onBlur={() =>
                          localCheckErrorFn(
                            `fileName${outerIndex}${index}`,
                            upload.customData
                          )
                        }
                      />
                      {fieldErrors?.[`fileName${outerIndex}${index}`]
                        ?.touched &&
                        fieldErrors?.[`fileName${outerIndex}${index}`]?.errors
                          ?.length > 0 && (
                          <span
                            className="errorPresent"
                            style={style.errorSpan}
                          >
                            {
                              fieldErrors[`fileName${outerIndex}${index}`]
                                ?.errors[0]
                            }
                          </span>
                        )}
                    </Col>

                    {!upload.proofofexperienceVerified && (
                      <Col xs={6} md={2}>
                        <Button
                          style={{ marginTop: "15px" }}
                          onClick={() => setShowIframe(index)}
                        >
                          <span className="ps-1">Verify</span>
                        </Button>
                      </Col>
                    )}
                    {upload.proofofexperienceVerified && (
                      <Col xs={6} md={2} style={{ marginTop: "18px" }}>
                        <Badge bg="success">
                          <FaCheckCircle size={"22"} className="me-2" />
                          Verified
                        </Badge>
                      </Col>
                    )}
                    <Col xs={6} md={2} className="ms-0 ps-0 text-start">
                      <Button
                        style={{ marginTop: "12px" }}
                        variant="outline"
                        onClick={() => setShowIframe(index)}
                      >
                        <IoEyeOutline size={"22"} />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDeleteUpload(index)}
                        style={{ marginTop: "12px" }}
                      >
                        <MdDeleteOutline size={"22"} />
                      </Button>
                    </Col>

                    {showIframe === index && (
                      <Modal
                        size="lg"
                        show={showIframe === index}
                        onHide={() => setShowIframe(null)}
                        centered
                      >
                        <Modal.Body className="text-center">
                          <iframe
                            src={upload.fileUrl}
                            width="100%"
                            style={{ border: "none", height: "80vh" }}
                            title="PDF Viewer"
                          />
                          {!upload.proofofexperienceVerified && (
                            <Button
                              className="mt-2"
                              onClick={() => confirmVerification(index)}
                            >
                              Confirm Verification
                            </Button>
                          )}
                          <Button
                            className="mt-2"
                            variant="secondary"
                            onClick={() => setShowIframe(null)}
                          >
                            Close
                          </Button>
                        </Modal.Body>
                      </Modal>
                    )}
                  </Row>
                </>
              )}

              {/* Show error if file is not verified */}
              {fieldErrors?.[`fileNotVerified${outerIndex}${index}`]?.touched &&
                fieldErrors?.[`fileNotVerified${outerIndex}${index}`]?.errors
                  ?.length > 0 && (
                  <span className="errorPresent" style={style.errorSpan}>
                    {
                      fieldErrors[`fileNotVerified${outerIndex}${index}`]
                        ?.errors[0]
                    }
                  </span>
                )}
            </Col>
          </Row>
        ))}
        <div className="text-start text-sm-end">
          <Button variant="primary" className="mt-3" onClick={handleAddUpload}>
            Add File
          </Button>
        </div>
      </div>
    </>
  );
};

export default MultipleFileUpload;
